import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { decodeBlobChunk } from "../lib/blob-transfer";
import { shouldPreserveConflictCopy } from "../lib/conflicts";
import { cryptoClient } from "../lib/crypto-client";
import { KeyedTaskQueue } from "../lib/keyed-task-queue";
import { leaseContactExpired, leaseOwnerChanged } from "../lib/lease-client";
import { flushOpenEditors } from "../lib/save-now";
import {
  discardDirtyRecords,
  getCursor,
  getDeviceId,
  getLeaseGeneration,
  localDb,
  setCursor,
  setLeaseGeneration,
  type LocalRecord
} from "../lib/local-db";
import { tagDefinitions } from "../lib/tags";
import type { FileRecord, PreferencesRecord, VaultRecord } from "../types";

// Smaller chunks give slow connections frequent progress updates and keep each
// encrypted request comfortably below intermediary timeout/body-size limits.
const chunkSize = 1024 * 1024;
const leaseHeartbeatMs = 5_000;
const disconnectedLockMs = 30_000;

function retryableTransferError(error: unknown): boolean {
  return !(error instanceof ApiError && [400, 401, 403, 404, 409, 413, 423].includes(error.status));
}

async function retryTransfer<T>(work: () => Promise<T>, onRetry: (attempt: number) => void): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try { return await work(); }
    catch (error) {
      lastError = error;
      if (attempt === 3 || !retryableTransferError(error)) throw error;
      onRetry(attempt + 1);
      await new Promise((resolve) => window.setTimeout(resolve, attempt * 600));
    }
  }
  throw lastError;
}

const defaultPreferences = (): PreferencesRecord => {
  const stamp = new Date().toISOString();
  return {
    id: "00000000-0000-4000-8000-000000000001",
    kind: "preferences",
    title: "Preferences",
    parentId: null,
    tags: [],
    favorite: false,
    createdAt: stamp,
    updatedAt: stamp,
    theme: "system",
    accent: "#0f6cbd",
    editorFont: "ui-monospace, SFMono-Regular, Consolas, monospace",
    editorSize: 14,
    lineWrap: true,
    tabSize: 2,
    density: "comfortable",
    sidebarWidth: 272,
    autoLockMinutes: 15,
    clipboardSeconds: 30,
    defaultSecretView: "simple",
    tagDefinitions: tagDefinitions(),
    wallpaper: { kind: "default", value: "" },
    desktopState: { windows: [], iconPositions: {}, iconSlots: { "this-vault": 0, trash: 1, "password-manager": 2, authenticator: 3, todo: 4, soundcloud: 5, filesharing: 6 }, appSizes: {}, usage: {}, editors: {}, searchQueries: {} }
  };
};

export type SyncState = "acquiring" | "syncing" | "saved" | "saving" | "local" | "offline" | "error";
type LeaseToken = { deviceId: string; generation: number };

function sameLocalVersion(left: LocalRecord, right: LocalRecord): boolean {
  return left.revision === right.revision
    && left.baseRevision === right.baseRevision
    && left.deleted === right.deleted
    && left.ciphertext === right.ciphertext;
}

function leaseErrorCode(error: ApiError): string | undefined {
  return (error.details as { code?: string } | undefined)?.code;
}

export function useVault(onLock: (message?: string) => void) {
  const [records, setRecords] = useState<VaultRecord[]>([]);
  const [syncState, setSyncState] = useState<SyncState>(navigator.onLine ? "acquiring" : "offline");
  const [syncMessage, setSyncMessage] = useState("");
  const [dirtyCount, setDirtyCount] = useState(0);
  const [ready, setReady] = useState(false);
  const syncing = useRef(false);
  const renewing = useRef(false);
  const syncPending = useRef(false);
  const syncRef = useRef<() => Promise<boolean>>(async () => false);
  const recordQueue = useRef(new KeyedTaskQueue());
  const lease = useRef<LeaseToken | null>(null);
  const lastServerContact = useRef(Date.now());
  const locked = useRef(false);
  const discarding = useRef(false);
  const freshlyUploadedBlobs = useRef(new Map<string, Blob>());

  const refreshDirtyCount = useCallback(async () => {
    const count = await localDb.records.where("dirty").equals(1).count();
    setDirtyCount(count);
    return count;
  }, []);

  const decryptLocal = useCallback(async () => {
    const rows = await localDb.records.toArray();
    const plain: VaultRecord[] = [];
    for (const row of rows) {
      if (row.deleted) continue;
      try {
        const record = await cryptoClient.decryptRecord(row.id, row.revision, row.ciphertext);
        if (record && typeof record === "object" && "kind" in record) plain.push(record);
      } catch {
        setSyncMessage(`Could not decrypt record ${row.id.slice(0, 8)}`);
      }
    }
    if (!plain.some((record) => record.kind === "preferences")) plain.push(defaultPreferences());
    setRecords(plain.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
  }, []);

  const lockForLeaseLoss = useCallback(async (message: string) => {
    if (locked.current) return;
    locked.current = true;
    discarding.current = true;
    const discarded = await discardDirtyRecords();
    lease.current = null;
    api.setLease(null);
    onLock(discarded > 0 ? `${message} ${discarded} unsynced local ${discarded === 1 ? "change was" : "changes were"} discarded.` : message);
  }, [onLock]);

  const lockKeepingLocal = useCallback(async (message: string) => {
    if (locked.current) return;
    locked.current = true;
    await flushOpenEditors().catch(() => undefined);
    lease.current = null;
    api.setLease(null);
    onLock(message);
  }, [onLock]);

  const acquireLease = useCallback(async () => {
    const deviceId = await getDeviceId();
    const previousGeneration = await getLeaseGeneration();
    const response = await api.acquireLease(deviceId);
    const changedOwner = leaseOwnerChanged(previousGeneration, response.generation);
    let discarded = 0;
    if (changedOwner) discarded = await discardDirtyRecords();
    await setLeaseGeneration(response.generation);
    lease.current = { deviceId, generation: response.generation };
    api.setLease(lease.current);
    lastServerContact.current = Date.now();
    if (discarded > 0) setSyncMessage(`${discarded} stale local ${discarded === 1 ? "change was" : "changes were"} discarded because another device edited the vault.`);
    return discarded;
  }, []);

  const pushDirty = useCallback(async () => {
    const ids = (await localDb.records.where("dirty").equals(1).primaryKeys()) as string[];
    for (const id of ids) {
      await recordQueue.current.run(id, async () => {
        const row = await localDb.records.get(id);
        if (!row?.dirty) return;
        try {
          const result = await api.putRecord(row.id, row.baseRevision, row.ciphertext, row.deleted);
          const latest = await localDb.records.get(row.id);
          if (latest?.dirty && sameLocalVersion(latest, row)) {
            await localDb.records.update(row.id, { revision: result.revision, baseRevision: result.revision, dirty: 0 });
          }
        } catch (error) {
          if (error instanceof ApiError && error.status === 409) {
            const current = (error.details as { current?: { revision: number; deleted: boolean; ciphertext: string } }).current;
            if (!current) return;
            if (current.revision === row.revision && current.deleted === row.deleted && current.ciphertext === row.ciphertext) {
              const latest = await localDb.records.get(row.id);
              if (latest?.dirty && sameLocalVersion(latest, row)) {
                await localDb.records.update(row.id, { revision: current.revision, baseRevision: current.revision, dirty: 0 });
              }
              return;
            }
            const localPlain = row.deleted ? null : await cryptoClient.decryptRecord(row.id, row.revision, row.ciphertext);
            const remotePlain = current.deleted ? null : await cryptoClient.decryptRecord(row.id, current.revision, current.ciphertext);
            await localDb.records.put({ id: row.id, revision: current.revision, baseRevision: current.revision, dirty: 0, deleted: current.deleted, ciphertext: current.ciphertext });
            if (localPlain && shouldPreserveConflictCopy(localPlain, remotePlain)) {
              const conflict: VaultRecord = {
                ...localPlain,
                id: crypto.randomUUID(),
                title: `${localPlain.title} (pre-lease conflict)`,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              };
              const ciphertext = await cryptoClient.encryptRecord(conflict, 1);
              await localDb.records.put({ id: conflict.id, revision: 1, baseRevision: 0, dirty: 1, deleted: false, ciphertext });
              setSyncMessage("A pre-lease concurrent edit was preserved as a conflict copy.");
            }
            return;
          }
          throw error;
        }
      });
    }
    await refreshDirtyCount();
  }, [refreshDirtyCount]);

  const pullChanges = useCallback(async () => {
    let cursor = await getCursor();
    let hasMore = true;
    while (hasMore) {
      const page = await api.changes(cursor);
      if (lease.current && Date.now() - lastServerContact.current >= 10_000) {
        await api.renewLease();
        lastServerContact.current = Date.now();
      }
      setSyncState("syncing");
      setSyncMessage(page.reset
        ? `Loading ${page.changes.length} current encrypted ${page.changes.length === 1 ? "record" : "records"}…`
        : page.changes.length > 0 ? `Applying ${page.changes.length} encrypted ${page.changes.length === 1 ? "change" : "changes"}…` : "Encrypted vault is up to date.");
      if (page.reset) await localDb.records.where("dirty").equals(0).delete();
      for (const change of page.changes) {
        const local = await localDb.records.get(change.id);
        if (local?.dirty) continue;
        await localDb.records.put({
          id: change.id,
          revision: change.revision,
          baseRevision: change.revision,
          dirty: 0,
          deleted: change.deleted,
          ciphertext: change.ciphertext
        });
      }
      cursor = page.cursor;
      await setCursor(cursor);
      hasMore = page.hasMore;
      if (lease.current && Date.now() - lastServerContact.current >= 10_000) {
        await api.renewLease();
        lastServerContact.current = Date.now();
      }
    }
  }, []);

  const sync = useCallback(async () => {
    if (syncing.current) {
      syncPending.current = true;
      return false;
    }
    if (!navigator.onLine) {
      setSyncState("offline");
      return false;
    }
    syncing.current = true;
    setSyncState(lease.current ? "saving" : "acquiring");
    try {
      await recordQueue.current.drain();
      const discarded = !lease.current ? await acquireLease() : 0;
      setSyncState("syncing");
      setSyncMessage("Edit control acquired. Loading the encrypted vault…");
      await pullChanges();
      await pushDirty();
      await pullChanges();
      setSyncMessage("Decrypting the local vault index…");
      await decryptLocal();
      lastServerContact.current = Date.now();
      const remaining = await refreshDirtyCount();
      setSyncState(remaining > 0 ? "local" : "saved");
      if (remaining === 0 && discarded === 0) setSyncMessage("All local changes are saved to the server.");
      return remaining === 0;
    } catch (error) {
      if (error instanceof ApiError && error.status === 423 && leaseErrorCode(error) === "LEASE_LOST") {
        await lockForLeaseLoss("Another device took control of the vault.");
      } else if (error instanceof ApiError && error.status === 423) {
        lease.current = null;
        api.setLease(null);
        setSyncState("offline");
        setSyncMessage("The edit lease expired. Local edits were kept and this device is reconnecting.");
      } else if (error instanceof ApiError && error.status === 401) {
        await lockKeepingLocal("Your server session expired. Local edits were kept on this device.");
      } else {
        setSyncState(navigator.onLine ? "error" : "offline");
        setSyncMessage(error instanceof Error ? error.message : "The server could not be reached");
      }
      return false;
    } finally {
      syncing.current = false;
      if (syncPending.current && !locked.current) {
        syncPending.current = false;
        queueMicrotask(() => void syncRef.current());
      }
    }
  }, [acquireLease, decryptLocal, lockForLeaseLoss, lockKeepingLocal, pullChanges, pushDirty, refreshDirtyCount]);
  syncRef.current = sync;

  const initialize = useCallback(async () => {
    await decryptLocal();
    await refreshDirtyCount();
    try {
      await sync();
    } finally {
      setReady(true);
    }
  }, [decryptLocal, refreshDirtyCount, sync]);

  const saveRecord = useCallback(async (record: VaultRecord) => {
    if (discarding.current) return record;
    const updated = { ...record, updatedAt: new Date().toISOString() } as VaultRecord;
    setRecords((current) => [updated, ...current.filter((item) => item.id !== updated.id)]);
    await recordQueue.current.run(updated.id, async () => {
      const local = await localDb.records.get(updated.id);
      const baseRevision = local?.dirty ? local.baseRevision : local?.revision ?? 0;
      const revision = baseRevision + 1;
      const ciphertext = await cryptoClient.encryptRecord(updated, revision);
      if (discarding.current) return;
      await localDb.records.put({ id: updated.id, revision, baseRevision, dirty: 1, deleted: false, ciphertext });
    });
    const count = await refreshDirtyCount();
    setSyncState(navigator.onLine ? "local" : "offline");
    setSyncMessage(`${count} local ${count === 1 ? "change is" : "changes are"} waiting to be saved.`);
    return updated;
  }, [refreshDirtyCount]);

  const softDelete = useCallback(async (record: VaultRecord) => {
    await saveRecord({ ...record, deletedAt: new Date().toISOString() });
  }, [saveRecord]);

  const restore = useCallback(async (record: VaultRecord) => {
    const restored = { ...record };
    delete restored.deletedAt;
    await saveRecord(restored);
  }, [saveRecord]);

  const permanentlyDelete = useCallback(async (record: VaultRecord) => {
    setRecords((current) => current.filter((item) => item.id !== record.id));
    await recordQueue.current.run(record.id, async () => {
      const local = await localDb.records.get(record.id);
      const baseRevision = local?.dirty ? local.baseRevision : local?.revision ?? 0;
      const revision = baseRevision + 1;
      const ciphertext = await cryptoClient.encryptRecord(record, revision);
      await localDb.records.put({ id: record.id, revision, baseRevision, dirty: 1, deleted: true, ciphertext });
    });
    await refreshDirtyCount();
    const saved = await sync();
    const confirmed = await localDb.records.get(record.id);
    if (saved && confirmed?.deleted && !confirmed.dirty && record.kind === "file") await api.deleteBlob(record.blobId);
  }, [refreshDirtyCount, sync]);

  const uploadFile = useCallback(async (file: File, parentId: string | null, onProgress: (value: number, phase?: string) => void) => {
    if (!lease.current) throw new Error("Connect to the server and acquire the edit lease before uploading files.");
    onProgress(0, "Reading file");
    const bytes = await file.arrayBuffer();
    onProgress(2, "Checking file integrity");
    const sha256 = await cryptoClient.sha256(bytes);
    const id = crypto.randomUUID();
    const blobId = crypto.randomUUID();
    const fileKey = await cryptoClient.newFileKey();
    const count = Math.max(1, Math.ceil(bytes.byteLength / chunkSize));
    onProgress(4, "Starting encrypted upload");
    await api.initBlob({ id: blobId, byteSize: bytes.byteLength, chunkSize, chunkCount: count });
    try {
      for (let index = 0; index < count; index++) {
        const part = bytes.slice(index * chunkSize, Math.min(bytes.byteLength, (index + 1) * chunkSize));
        const envelope = await cryptoClient.encryptChunk(fileKey, blobId, index, part);
        let highestSent = 0;
        await retryTransfer(
          () => api.putChunk(blobId, index, envelope, (loaded, total) => {
            const sent = total > 0 ? Math.min(1, loaded / total) : 0;
            highestSent = Math.max(highestSent, sent);
            const fileRatio = (index + highestSent) / count;
            onProgress(Math.round(4 + fileRatio * 92), "Uploading encrypted file");
          }),
          (attempt) => onProgress(Math.round(4 + (index / count) * 92), `Connection interrupted — retrying (${attempt}/3)`)
        );
        lastServerContact.current = Date.now();
        onProgress(Math.round(4 + ((index + 1) / count) * 92), "Uploading encrypted file");
      }
      onProgress(97, "Finalizing upload");
      await api.completeBlob(blobId);
      lastServerContact.current = Date.now();
      const stamp = new Date().toISOString();
      const record: FileRecord = {
        id, kind: "file", title: file.name, parentId, tags: [], favorite: false,
        createdAt: stamp, updatedAt: stamp, mime: file.type || "application/octet-stream",
        size: file.size, blobId, fileKey, chunkCount: count, chunkSize, sha256
      };
      onProgress(99, "Saving file information");
      await saveRecord(record);
      // The shell opens a successful upload immediately. Reuse the browser's
      // original blob once instead of downloading and decrypting it again.
      freshlyUploadedBlobs.current.set(blobId, file);
      onProgress(100, "Upload complete");
      return record;
    } catch (error) {
      await api.deleteBlob(blobId).catch(() => undefined);
      throw error;
    }
  }, [saveRecord]);

  const downloadFile = useCallback(async (record: FileRecord, onProgress?: (value: number, phase?: string) => void) => {
    const fresh = freshlyUploadedBlobs.current.get(record.blobId);
    if (fresh) {
      freshlyUploadedBlobs.current.delete(record.blobId);
      onProgress?.(100, "Ready");
      return fresh.slice(0, fresh.size, record.mime);
    }
    const chunks: Uint8Array[] = [];
    let length = 0;
    onProgress?.(0, "Connecting to encrypted storage");
    for (let index = 0; index < record.chunkCount; index++) {
      const response = await retryTransfer(
        () => api.getChunk(record.blobId, index, (loaded, total) => {
          const expectedWireBytes = Math.max(1, Math.min(record.chunkSize, record.size - index * record.chunkSize) * 1.36);
          const received = total > 0 ? Math.min(1, loaded / total) : Math.min(.98, loaded / expectedWireBytes);
          onProgress?.(Math.round((index + received) / record.chunkCount * 90), "Downloading encrypted file");
        }),
        (attempt) => onProgress?.(Math.round(index / record.chunkCount * 92), `Connection interrupted — retrying (${attempt}/3)`)
      );
      lastServerContact.current = Date.now();
      const envelope = decodeBlobChunk(response, index);
      const bytes = new Uint8Array(await cryptoClient.decryptChunk(record.fileKey, record.blobId, index, envelope));
      chunks.push(bytes);
      length += bytes.byteLength;
      onProgress?.(Math.round((index + 1) / record.chunkCount * 92), "Loading and decrypting file");
    }
    lastServerContact.current = Date.now();
    onProgress?.(94, "Assembling file");
    const output = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) { output.set(chunk, offset); offset += chunk.byteLength; }
    onProgress?.(97, "Verifying file integrity");
    if (await cryptoClient.sha256(output.buffer) !== record.sha256) throw new Error("File integrity check failed");
    onProgress?.(100, "Ready");
    return new Blob([output], { type: record.mime });
  }, []);

  const renewLease = useCallback(async () => {
    if (locked.current || !ready || renewing.current) return;
    if (!navigator.onLine) {
      setSyncState("offline");
      return;
    }
    if (!lease.current) {
      await sync();
      return;
    }
    renewing.current = true;
    try {
      await api.renewLease();
      lastServerContact.current = Date.now();
      if (!syncing.current) setSyncState((current) => current === "offline" || current === "error" || current === "acquiring" ? (dirtyCount > 0 ? "local" : "saved") : current);
    } catch (error) {
      if (error instanceof ApiError && error.status === 423 && leaseErrorCode(error) === "LEASE_LOST") await lockForLeaseLoss("Another device took control of the vault.");
      else if (error instanceof ApiError && error.status === 423) {
        lease.current = null;
        api.setLease(null);
        setSyncState("offline");
      }
      else if (error instanceof ApiError && error.status === 401) await lockKeepingLocal("Your server session expired. Local edits were kept on this device.");
      else setSyncState("offline");
    } finally {
      renewing.current = false;
    }
  }, [dirtyCount, lockForLeaseLoss, lockKeepingLocal, ready, sync]);

  const releaseLease = useCallback(async () => {
    // Do not send a delayed release that could expire a rapid same-device
    // re-acquisition. Other devices can preempt immediately; otherwise the
    // short server TTL releases this device naturally.
    lease.current = null;
    api.setLease(null);
  }, []);

  useEffect(() => {
    if (!ready) return;
    const heartbeat = window.setInterval(() => void renewLease(), leaseHeartbeatMs);
    const timeout = window.setInterval(() => {
      if (!locked.current && leaseContactExpired(lastServerContact.current, Date.now(), disconnectedLockMs)) {
        void lockKeepingLocal("Locked because the edit lease could not be confirmed for 30 seconds. Local edits were kept on this device.");
      }
    }, 1_000);
    const online = () => void renewLease();
    const offline = () => setSyncState("offline");
    const visible = () => {
      if (document.visibilityState !== "visible") return;
      if (leaseContactExpired(lastServerContact.current, Date.now(), disconnectedLockMs)) {
        void lockKeepingLocal("Locked because this tab could not renew its edit lease. Local edits were kept on this device.");
      } else {
        void renewLease();
      }
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    window.addEventListener("focus", online);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.clearInterval(heartbeat);
      window.clearInterval(timeout);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
      window.removeEventListener("focus", online);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [lockKeepingLocal, ready, renewLease]);

  const preferences = useMemo(() => records.find((record): record is PreferencesRecord => record.kind === "preferences") ?? defaultPreferences(), [records]);

  return {
    records, preferences, ready, syncState, syncMessage, dirtyCount,
    initialize, saveRecord, softDelete, restore, permanentlyDelete,
    uploadFile, downloadFile, sync, releaseLease
  };
}
